import { EVENT5K } from "@/constants/testIds";
import { Sparkles, TrendingUp, HandCoins, Users } from "lucide-react";

const PILLARS = [
  { icon: Sparkles, title: "Training & Coaching", desc: "Practical business education for founders at every stage." },
  { icon: TrendingUp, title: "Grow & Sustain", desc: "Playbooks and mentorship to build lasting businesses." },
  { icon: HandCoins, title: "Cash Awards & Capital", desc: "Access to funding, cash awards, and capital partners." },
  { icon: Users, title: "Community & Connections", desc: "Rooms of resources, buyers, and champions of your work." },
];

export default function Overview() {
  return (
    <section
      id="overview"
      data-testid={EVENT5K.sectionOverview}
      className="relative bg-[#0A0A0A] py-24 md:py-32 border-t border-[#27272A]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#00A651] font-bold mb-4">
              The Mission
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white">
              Not just a race.<br />
              <span className="text-[#EC008C]">A movement.</span>
            </h2>
            <div className="mt-6 h-1 w-16 bg-[#00A651]" />

            <div className="relative mt-10 grid grid-cols-2 gap-3">
              <div className="relative group overflow-hidden border border-[#27272A] col-span-2">
                <img
                  src="/images/adult-runners.jpg"
                  alt="Adult walkers at the 2025 Run to Empower Entrepreneurs 5K"
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#00A651] font-bold">
                    2025 · Adult Walkers
                  </div>
                  <div className="mt-1 font-display text-xl md:text-2xl font-bold uppercase text-white leading-tight">
                    Every step fuels a founder.
                  </div>
                </div>
              </div>

              <div className="relative group overflow-hidden border border-[#27272A]">
                <img
                  src="/images/josiah-runner.jpg"
                  alt="Josiah, youth runner Bib #235 at the 2025 Run to Empower Entrepreneurs 5K"
                  loading="lazy"
                  className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <div className="absolute bottom-2 left-3 right-3">
                  <div className="text-[9px] uppercase tracking-[0.28em] text-[#EC008C] font-bold">
                    Bib #235
                  </div>
                  <div className="font-display text-sm font-bold uppercase text-white leading-tight">
                    Youth strong.
                  </div>
                </div>
              </div>

              <div className="relative group overflow-hidden border border-[#27272A]">
                <img
                  src="/images/finish-line-2025.png"
                  alt="Kids running through the FINISH arch at the 2025 Run to Empower Entrepreneurs 5K"
                  loading="lazy"
                  className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <div className="absolute bottom-2 left-3 right-3">
                  <div className="text-[9px] uppercase tracking-[0.28em] text-[#00A651] font-bold">
                    Finish Line · 2025
                  </div>
                  <div className="font-display text-sm font-bold uppercase text-white leading-tight">
                    Kids leading the way.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            <p className="text-lg md:text-xl text-[#E5E5E5] leading-relaxed">
              Every mile you run, walk, or cheer through fuels the{" "}
              <span className="text-[#00A651] font-semibold">Freedom In Me Business Showers</span>{" "}
              mission — helping entrepreneurs start, grow, and sustain successful businesses.
            </p>
            <p className="text-base md:text-lg text-[#A3A3A3] leading-relaxed">
              We connect new businesses to <em className="not-italic text-white">people and resources</em> through training, coaching,
              community, cash awards, and access to funding and capital. The Run to Empower Entrepreneurs 5K
              turns wellness into economic mobility — and it takes the whole community to move the movement.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              {PILLARS.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="bg-[#141414] border border-[#27272A] p-5 hover:border-[#00A651]/60 transition-colors">
                  <Icon className="w-6 h-6 text-[#EC008C] mb-3" />
                  <div className="font-display font-bold uppercase text-white text-lg">{title}</div>
                  <div className="text-sm text-[#A3A3A3] mt-1">{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
